import { createReducer, on } from '@ngrx/store';
import { RuleBuilderState, initialRuleBuilderState, WizardStep } from './rule-builder.state';
import { RuleConfiguration } from '@core/models/rule-configuration.models';
import * as RuleBuilderActions from './rule-builder.actions';

export const ruleBuilderReducer = createReducer(
  initialRuleBuilderState,

  // ============================================================================
  // Initialization & Reference Data
  // ============================================================================

  on(RuleBuilderActions.initializeWizard, () => ({
    ...initialRuleBuilderState
  })),

  on(RuleBuilderActions.loadRuleForEdit, (state, { ruleId }) => ({
    ...state,
    isLoading: true,
    error: null,
    editRuleId: ruleId,
    isEditMode: true
  })),

  on(RuleBuilderActions.loadRuleSuccess, (state, { rule }) => {
    const config = typeof rule.configuration === 'string' 
      ? JSON.parse(rule.configuration) 
      : rule.configuration;

    return {
      ...state,
      selectedRuleType: rule.ruleType as any,
      categoryId: rule.categoryId || null,
      ruleName: rule.name,
      ruleDescription: rule.description,
      targetSheets: rule.targetSheets,
      configuration: config,
      activateImmediately: rule.isActive,
      isLoading: false,
      error: null
    };
  }),

  on(RuleBuilderActions.loadRuleFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  on(RuleBuilderActions.loadCategories, (state) => ({
    ...state,
    isLoading: true
  })),

  on(RuleBuilderActions.loadCategoriesSuccess, (state, { categories }) => ({
    ...state,
    categories,
    categoriesLoaded: true,
    isLoading: false
  })),

  on(RuleBuilderActions.loadCategoriesFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false
  })),

  on(RuleBuilderActions.loadTemplates, (state) => ({
    ...state,
    isLoading: true
  })),

  on(RuleBuilderActions.loadTemplatesSuccess, (state, { templates }) => ({
    ...state,
    templates,
    templatesLoaded: true,
    isLoading: false
  })),

  on(RuleBuilderActions.loadTemplatesFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false
  })),

  // ============================================================================
  // Step 1: Choose Rule Type
  // ============================================================================

  on(RuleBuilderActions.selectRuleType, (state, { ruleType }) => ({
    ...state,
    selectedRuleType: ruleType,
    selectedTemplateId: null,
    selectedTemplate: null,
    configuration: null, // Reset configuration when type changes
    isDirty: true
  })),

  on(RuleBuilderActions.selectTemplate, (state, { templateId }) => ({
    ...state,
    selectedTemplateId: templateId,
    isDirty: true
  })),

  on(RuleBuilderActions.loadTemplateDetailsSuccess, (state, { template }) => {
    const config = typeof template.templateConfiguration === 'string'
      ? JSON.parse(template.templateConfiguration)
      : template.templateConfiguration;

    return {
      ...state,
      selectedTemplate: template,
      selectedRuleType: template.ruleType as any,
      categoryId: template.categoryId || state.categoryId,
      configuration: config,
      isLoading: false
    };
  }),

  on(RuleBuilderActions.loadTemplateDetailsFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false
  })),

  // ============================================================================
  // Step 2: Category & Basic Info
  // ============================================================================

  on(RuleBuilderActions.updateCategoryId, (state, { categoryId }) => ({
    ...state,
    categoryId,
    isDirty: true
  })),

  on(RuleBuilderActions.updateRuleName, (state, { name }) => ({
    ...state,
    ruleName: name,
    isDirty: true
  })),

  on(RuleBuilderActions.updateRuleDescription, (state, { description }) => ({
    ...state,
    ruleDescription: description,
    isDirty: true
  })),

  on(RuleBuilderActions.updateTargetSheets, (state, { targetSheets }) => ({
    ...state,
    targetSheets,
    isDirty: true
  })),

  // ============================================================================
  // Step 3: Configure Parameters
  // ============================================================================

  on(RuleBuilderActions.updateConfiguration, (state, { configuration }) => ({
    ...state,
    configuration,
    isDirty: true
  })),

  on(RuleBuilderActions.parseFormulaSuccess, (state, { sources }) => {
    // Update configuration with parsed sources (for CrossSheetAggregation)
    const config = state.configuration as any;
    return {
      ...state,
      configuration: {
        ...config,
        sources
      },
      isDirty: true
    };
  }),

  on(RuleBuilderActions.parseFormulaFailure, (state, { error }) => ({
    ...state,
    error
  })),

  // ============================================================================
  // Step 4: Test Rule
  // ============================================================================

  on(RuleBuilderActions.uploadTestFile, (state) => ({
    ...state,
    testFileUploaded: true,
    testLoading: true,
    testResults: null
  })),

  on(RuleBuilderActions.testRuleSuccess, (state, { results }) => ({
    ...state,
    testResults: results,
    testLoading: false
  })),

  on(RuleBuilderActions.testRuleFailure, (state, { error }) => ({
    ...state,
    error,
    testLoading: false
  })),

  on(RuleBuilderActions.clearTestResults, (state) => ({
    ...state,
    testFileUploaded: false,
    testResults: null
  })),

  // ============================================================================
  // Step 5: Review
  // ============================================================================

  on(RuleBuilderActions.markAsReviewed, (state) => ({
    ...state,
    reviewed: true
  })),

  // ============================================================================
  // Step 6: Save & Activate
  // ============================================================================

  on(RuleBuilderActions.toggleActivateImmediately, (state, { activate }) => ({
    ...state,
    activateImmediately: activate
  })),

  on(RuleBuilderActions.toggleSaveAsTemplate, (state, { saveAsTemplate }) => ({
    ...state,
    saveAsTemplate
  })),

  on(RuleBuilderActions.saveRule, (state) => ({
    ...state,
    isSaving: true,
    error: null
  })),

  on(RuleBuilderActions.saveRuleSuccess, (state) => ({
    ...state,
    isSaving: false,
    isDirty: false,
    error: null
  })),

  on(RuleBuilderActions.saveRuleFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error
  })),

  // ============================================================================
  // Navigation
  // ============================================================================

  on(RuleBuilderActions.goToStep, (state, { step }) => ({
    ...state,
    currentStep: step
  })),

  on(RuleBuilderActions.nextStep, (state) => {
    const nextStep = Math.min(state.currentStep + 1, WizardStep.Save);
    return {
      ...state,
      currentStep: nextStep
    };
  }),

  on(RuleBuilderActions.previousStep, (state) => {
    const prevStep = Math.max(state.currentStep - 1, WizardStep.ChooseType);
    return {
      ...state,
      currentStep: prevStep
    };
  }),

  on(RuleBuilderActions.jumpToStep, (state, { step }) => ({
    ...state,
    currentStep: step
  })),

  // ============================================================================
  // Validation & Errors
  // ============================================================================

  on(RuleBuilderActions.setValidationErrors, (state, { errors }) => ({
    ...state,
    validationErrors: errors
  })),

  on(RuleBuilderActions.clearValidationErrors, (state) => ({
    ...state,
    validationErrors: {}
  })),

  on(RuleBuilderActions.setError, (state, { error }) => ({
    ...state,
    error
  })),

  on(RuleBuilderActions.clearError, (state) => ({
    ...state,
    error: null
  })),

  // ============================================================================
  // Utility
  // ============================================================================

  on(RuleBuilderActions.initializeWizardFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  on(RuleBuilderActions.clearTemplate, (state) => ({
    ...state,
    selectedTemplate: null
  })),

  on(RuleBuilderActions.createRuleFromTemplate, (state) => ({
    ...state,
    isLoading: true
  })),

  on(RuleBuilderActions.createRuleFromTemplateSuccess, (state, { rule }) => ({
    ...state,
    isLoading: false,
    ruleName: rule.name || '',
    ruleDescription: rule.description || '',
    categoryId: rule.categoryId || null,
    targetSheets: rule.targetSheets || [],
    configuration: (typeof rule.configuration === 'string' 
      ? JSON.parse(rule.configuration)
      : rule.configuration) as RuleConfiguration || null,
    currentStep: WizardStep.BasicInfo // Move to next step
  })),

  on(RuleBuilderActions.createRuleFromTemplateFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  on(RuleBuilderActions.selectCategory, (state, { categoryId }) => ({
    ...state,
    categoryId,
    isDirty: true
  })),

  on(RuleBuilderActions.updateIsActive, (state, { isActive }) => ({
    ...state,
    activateImmediately: isActive,
    isDirty: true
  })),

  on(RuleBuilderActions.updateDisplayOrder, (state, { displayOrder }) => ({
    ...state,
    isDirty: true
    // Note: displayOrder not in current state, ignoring for now
  })),

  on(RuleBuilderActions.cancelWizard, () => ({
    ...initialRuleBuilderState
  })),

  on(RuleBuilderActions.resetWizard, () => ({
    ...initialRuleBuilderState
  })),

  on(RuleBuilderActions.setDirty, (state, { isDirty }) => ({
    ...state,
    isDirty
  }))
);
